import { createAsyncThunk } from '@reduxjs/toolkit'
import axiosInstance from 'api/apiCommon'
import { type PaginatedResponse } from 'common/utils/types'
import { transformDescription } from 'utils/data-transformator'

import {
  type TenderProject,
  type Bid,
  type UpsertProposalRequest,
  type Proposal,
  type ProposalTender,
  type TenderProjectDT,
} from './bid.types'

interface GetBidListPayload {
  ipp: number
  page: number
  accepted: boolean
}

export const getBidList = createAsyncThunk(
  'vendor/bidList',
  async ({
    ipp,
    page,
    accepted,
  }: GetBidListPayload): Promise<{ page: number; list: PaginatedResponse<Bid[]> }> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .get<PaginatedResponse<Bid[]>>(
          `/vendors/bid-list?accepted=${accepted as unknown as string}&ipp=${ipp}&page=${page}`,
        )
        .then((data) => {
          resolve({ page, list: data })
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

interface UpsertProposalPayload {
  tenderId: string
  payload?: UpsertProposalRequest
}

export const upsertProposal = createAsyncThunk(
  'tender/upsert',
  async ({ tenderId, payload }: UpsertProposalPayload): Promise<Proposal> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .put<Proposal>(`/tenders/${tenderId}/proposal`, payload)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

interface SubmitProposalPayload {
  tenderId: string
}
export const submitProposal = createAsyncThunk(
  'tender/submit',
  async ({ tenderId }: SubmitProposalPayload) => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .put(`/tenders/${tenderId}/proposal/submit`)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

interface GetProposalPayload {
  tenderId: string
}
export const getProposalInfo = createAsyncThunk(
  'tender/getProposalInfo',
  async ({ tenderId }: GetProposalPayload): Promise<Proposal> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .get<Proposal>(`/tenders/${tenderId}/proposal`)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

interface GetTenderPayload {
  tenderId: string
}
export const getTenderById = createAsyncThunk(
  'tender/getTenderById',
  async ({ tenderId }: GetTenderPayload): Promise<ProposalTender> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .get<ProposalTender>(`/tenders/${tenderId}`)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

interface BidAnswerPayload {
  projectId: string
  answer: boolean
}

export const bidAnswer = createAsyncThunk(
  'vendor/bidAnswer',
  async ({ projectId, answer }: BidAnswerPayload): Promise<string> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .put('/vendors/bid-answer', {
          projectId,
          answer,
        })
        .then(() => {
          resolve(projectId)
        })
        .catch((err) => {
          switch (err.code) {
            case 'vendor-missing-topics':
              err.message = 'Vendor ne zadovoljava sve oblasti za projekat'
              reject(err)
              return
            default:
              reject(err)
          }
        })
    })
  },
)

interface GetProjectPayload {
  bidId: string
}

export const fetchTenderProject = createAsyncThunk(
  'vendor/fetchProjectInfo',
  async ({ bidId }: GetProjectPayload): Promise<TenderProject> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .get<TenderProjectDT>(`/projects/${bidId}`)
        .then((data) => {
          const transformedDescription = transformDescription(data.description)
          const transformedData = { ...data, description: transformedDescription }
          resolve(transformedData)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)
